/**
 * Marketing Upload Ticket page — PUBLIC (no login required).
 * The ticket ID in the URL is the auth. Single-use, 30-minute TTL.
 *
 * Flow:
 * 1. Claude creates a ticket via MCP `create_upload_ticket` with pre-set
 *    title / platform / audience / description / tags.
 * 2. Claude gives the user the URL like /marketing/upload/<ticket_id>.
 * 3. User opens the URL, drags a file, hits Upload.
 * 4. File lands in the Social Assets Library. Ticket is consumed.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { UploadCloud, CheckCircle2, XCircle, Loader2, FileIcon, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PLATFORM_LABELS = {
  instagram:   'Instagram',
  reel_tiktok: 'Reels / TikTok',
  facebook:    'Facebook',
  linkedin:    'LinkedIn',
  whatsapp:    'WhatsApp',
  twitter:     'Twitter / X',
  internal:    'Internal',
};

const AUDIENCE_LABELS = {
  students: 'Students',
  agents: 'Agents',
  universities: 'Universities',
  internal: 'Internal',
};

export default function MarketingUploadTicketPage() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [ticketError, setTicketError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [uploadErr, setUploadErr] = useState(null);

  const loadTicket = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/marketing/upload-ticket/${ticketId}`);
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Ticket not found');
      setTicket(data);
      setTicketError(null);
    } catch (e) {
      setTicketError(e.message || 'Ticket invalid or expired');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${BACKEND_URL}/api/marketing/upload-ticket/${ticketId}`, {
        method: 'POST',
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Upload failed');
      setSuccess(data.asset);
    } catch (e) {
      setUploadErr(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  if (loading) {
    return (
      <Shell>
        <div className="flex flex-col items-center py-16 text-slate-500">
          <Loader2 className="w-8 h-8 animate-spin mb-3" />
          <p>Loading upload ticket…</p>
        </div>
      </Shell>
    );
  }

  if (ticketError) {
    return (
      <Shell>
        <div className="flex flex-col items-center py-12 text-center">
          <XCircle className="w-14 h-14 text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">This link isn&apos;t valid</h2>
          <p className="text-slate-500 max-w-sm">{ticketError}</p>
          <p className="text-xs text-slate-400 mt-4">
            Upload tickets are single-use and expire 30 minutes after Claude creates them.
            Ask Claude to generate a fresh one.
          </p>
        </div>
      </Shell>
    );
  }

  if (success) {
    return (
      <Shell>
        <div className="flex flex-col items-center py-10 text-center">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-1">Uploaded 🎉</h2>
          <p className="text-slate-500 mb-6">
            &ldquo;{success.title}&rdquo; is now in your{' '}
            <strong>{PLATFORM_LABELS[success.platform] || success.platform}</strong> folder.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-left w-full max-w-md text-sm">
            <Row label="Title" value={success.title} />
            <Row label="Platform" value={PLATFORM_LABELS[success.platform] || success.platform} />
            <Row label="Audience" value={AUDIENCE_LABELS[success.audience] || success.audience} />
            <Row label="Size" value={fmtBytes(success.size_bytes)} />
            <Row label="Format" value={(success.format || '').toUpperCase()} />
          </div>
          <p className="text-xs text-slate-400 mt-6">
            You can close this tab. This link cannot be used again.
          </p>
        </div>
      </Shell>
    );
  }

  const exp = ticket.expires_at ? new Date(ticket.expires_at) : null;

  return (
    <Shell>
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 text-xs font-semibold text-indigo-700 bg-indigo-100 border border-indigo-200 px-3 py-1 rounded-full mb-3">
          <UploadCloud className="w-3.5 h-3.5" /> Upload ticket from Claude AI
        </div>
        <h1 className="text-2xl font-bold text-slate-800">{ticket.title}</h1>
        <p className="text-sm text-slate-500 mt-1">
          Drag a file below, or click to pick one. It will be filed with the metadata Claude prepared.
        </p>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm mb-6">
        <Row label="Platform" value={PLATFORM_LABELS[ticket.platform] || ticket.platform} />
        <Row label="Audience" value={AUDIENCE_LABELS[ticket.audience] || ticket.audience} />
        {ticket.description && <Row label="Description" value={ticket.description} />}
        {ticket.tags?.length > 0 && (
          <Row label="Tags" value={ticket.tags.join(', ')} />
        )}
        {exp && (
          <Row
            label="Expires"
            value={
              <span className="inline-flex items-center gap-1 text-amber-700">
                <Clock className="w-3.5 h-3.5" /> {exp.toLocaleString()}
              </span>
            }
          />
        )}
      </div>

      <label
        htmlFor="file-input"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={
          'block border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition ' +
          (dragOver
            ? 'border-indigo-500 bg-indigo-50'
            : 'border-slate-300 hover:border-indigo-400 hover:bg-indigo-50/40')
        }
      >
        <UploadCloud className="w-10 h-10 mx-auto text-slate-400 mb-2" />
        <p className="font-semibold text-slate-700">
          {file ? file.name : 'Drag & drop or click to pick a file'}
        </p>
        <p className="text-xs text-slate-500 mt-1">
          {file ? fmtBytes(file.size) : 'Max 25 MB · images, PDFs, videos'}
        </p>
        <input
          id="file-input"
          type="file"
          className="hidden"
          accept="image/*,application/pdf,video/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
      </label>

      {file && (
        <div className="mt-4 flex items-center gap-2 text-sm text-slate-600 bg-white border border-slate-200 rounded-lg p-3">
          <FileIcon className="w-4 h-4 text-slate-400" />
          <span className="flex-1 truncate">{file.name}</span>
          <button className="text-slate-500 hover:text-red-600 text-xs" onClick={() => setFile(null)}>
            remove
          </button>
        </div>
      )}

      {uploadErr && (
        <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{uploadErr}</span>
        </div>
      )}

      <button
        onClick={upload}
        disabled={!file || uploading}
        className={
          'mt-6 w-full py-3 rounded-lg font-semibold transition ' +
          (!file || uploading
            ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
            : 'bg-indigo-600 text-white hover:bg-indigo-700')
        }
      >
        {uploading ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
          </span>
        ) : 'Upload to the CRM'}
      </button>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-start justify-center p-6">
      <div className="w-full max-w-xl bg-white shadow-xl rounded-2xl p-8 mt-16" data-testid="upload-ticket-page">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-slate-100 last:border-none">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium text-right">{value}</span>
    </div>
  );
}

function fmtBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
